import { Banner, Button, Dialog, Field, Input, Select } from '@cloudflare/kumo';
import { useEffect, useState } from 'react';
import { ICONS } from './ui/icons';
import { ORG_ROLES } from '../lib/orgRoles';

export interface InviteResult {
  email: string;
  link: string;
  emailSent: boolean;
  emailError?: string;
}

interface InviteMemberModalProps {
  open: boolean;
  organizationName?: string;
  canInviteOwners: boolean;
  /** Resultado de um convite/reenvio para mostrar o link (quando o e-mail não saiu) */
  result: InviteResult | null;
  onSubmit: (email: string, role: string) => Promise<void>;
  onClose: () => void;
}

/** Convidar membro, no formato do "Invite to Organization" do Atlas. */
export function InviteMemberModal({ open, organizationName, canInviteOwners, result, onSubmit, onClose }: InviteMemberModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('MEMBER');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open && !result) { setEmail(''); setRole('MEMBER'); }
    setCopied(false);
  }, [open, result]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try { await onSubmit(email.trim(), role); } finally { setSubmitting(false); }
  };

  const copy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
    } catch {
      // Sem permissão de área de transferência: seleciona o campo para copiar à mão
      (document.getElementById('invite-link') as HTMLInputElement | null)?.select();
    }
  };

  const roleOptions = ORG_ROLES
    .filter((r) => canInviteOwners || r.value !== 'OWNER')
    .map((r) => ({ value: r.value, label: r.label, description: r.description }));

  const header = result
    ? <h3 className="text-lg font-semibold">{result.emailSent ? 'Convite enviado' : 'Convite criado'}</h3>
    : <h3 className="text-lg font-semibold">Convidar para a organização</h3>;

  return (
    <Dialog.Root open={open} onOpenChange={(o: boolean) => { if (!o) (onClose)(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{header}</Dialog.Title>

      {result ? (
        <div>
          {result.emailSent ? (
            <p className="text-kumo-subtle" style={{ marginBottom: 16 }}>
              Enviamos o convite para <strong>{result.email}</strong>. Ele vale por 7 dias. Se preferir, você também pode mandar o link abaixo.
            </p>
          ) : (
            <div style={{ marginBottom: 16 }}>
              <Banner variant="alert" description={<>
                O e-mail não foi enviado{result.emailError ? ` (${result.emailError})` : ''}. Copie o link e envie para <strong>{result.email}</strong>. Ele vale por 7 dias.
              </>} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <Field label="Link do convite">
                <Input aria-label="Link do convite"  value={result.link} readOnly onFocus={(e) => e.target.select()} />
              </Field>
            </div>
            <Button variant="secondary" icon={copied ? ICONS.Checkmark : ICONS.Copy} onClick={copy}>
              {copied ? 'Copiado' : 'Copiar'}
            </Button>
          </div>
          <div className="flex justify-end" style={{ marginTop: 24 }}>
            <Button variant="primary" onClick={onClose}>Concluir</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit}>
          <p className="text-kumo-subtle" style={{ marginBottom: 20 }}>
            {organizationName ? <>A pessoa vai entrar em <strong>{organizationName}</strong>. </> : null}
            Quem ainda não tem conta cria a senha ao aceitar.
          </p>
          <div className="space-y-4">
            <Field label="E-mail">
              <Input onChange={(e) => setEmail(e.target.value)} aria-label="E-mail" type="email" placeholder="nome@empresa.com.br" value={email} autoFocus />
            </Field>
            <Field label="Papel na organização">
              <Select value={(roleOptions.find((o) => o.value === role) ?? null)?.value ?? null} items={roleOptions} onValueChange={(value: string | null) => setRole(value ?? role)} />
            </Field>
          </div>
          <div className="flex justify-end gap-2" style={{ marginTop: 24 }}>
            <Button variant="secondary" type="button" onClick={onClose}>Cancelar</Button>
            <Button type="submit" variant="primary" loading={submitting} disabled={!email.trim()}>Enviar convite</Button>
          </div>
        </form>
      )}
    
</Dialog>
</Dialog.Root>
  );
}
